import { UserProfile, useUpdateProfile } from '../hooks/useProfile';

interface NotificationsCardProps {
  profile: UserProfile;
}

interface ToggleSwitchProps {
  checked: boolean;
  onChange: () => void;
  disabled: boolean;
}

function ToggleSwitch({ checked, onChange, disabled }: ToggleSwitchProps) {
  return (
    <button
      onClick={onChange}
      disabled={disabled}
      className={`relative w-12 h-6 rounded-full transition-colors ${
        checked ? 'bg-purple-600' : 'bg-slate-600'
      }`}
      role="switch"
      aria-checked={checked}
    >
      <span
        className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
          checked ? 'translate-x-6' : ''
        }`}
      />
    </button>
  );
}

export default function NotificationsCard({ profile }: NotificationsCardProps) {
  const updateProfile = useUpdateProfile();

  // Use profile values with defaults matching backend DEFAULT_NOTIFICATION_PREFERENCES
  const emailNewStories = profile.notifications?.emailNewStories ?? true;
  const emailStoryUpdates = profile.notifications?.emailStoryUpdates ?? true;
  const pushEnabled = profile.notifications?.pushEnabled ?? true;

  const handleToggle = async (
    field: 'emailNewStories' | 'emailStoryUpdates' | 'pushEnabled',
    currentValue: boolean
  ) => {
    await updateProfile.mutateAsync({ notifications: { [field]: !currentValue } });
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-6">
      <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <svg
          className="w-5 h-5 text-purple-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        Notifications
      </h2>

      <div className="space-y-4">
        {/* Email: New Stories */}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-white">New stories</div>
            <div className="text-sm text-purple-400">
              Get notified when new stories are available
            </div>
          </div>
          <ToggleSwitch
            checked={emailNewStories}
            onChange={() => handleToggle('emailNewStories', emailNewStories)}
            disabled={updateProfile.isPending}
          />
        </div>

        {/* Email: Story Updates */}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-white">Story updates</div>
            <div className="text-sm text-purple-400">
              Get notified about updates to stories you follow
            </div>
          </div>
          <ToggleSwitch
            checked={emailStoryUpdates}
            onChange={() => handleToggle('emailStoryUpdates', emailStoryUpdates)}
            disabled={updateProfile.isPending}
          />
        </div>

        {/* Push Notifications */}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-white">Push notifications</div>
            <div className="text-sm text-purple-400">Receive push notifications on your device</div>
          </div>
          <ToggleSwitch
            checked={pushEnabled}
            onChange={() => handleToggle('pushEnabled', pushEnabled)}
            disabled={updateProfile.isPending}
          />
        </div>
      </div>
    </div>
  );
}
